import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Flame } from 'lucide-react';
import { useAdminAuth } from '../../../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKLY_TOAST_STORAGE_KEY = 'voucher_first_redemption_toast_shown';

const VoucherPerformanceStat = () => {
  const { admin } = useAdminAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Scoped per-username so a shared browser (e.g. Agent then Manager
    // logging in) never suppresses/misattributes the other's celebration.
    const storageKey = `${WEEKLY_TOAST_STORAGE_KEY}:${admin?.username || 'unknown'}`;
    const fetchStats = () => {
      axios.get(`${BACKEND_URL}/api/admin/vouchers/mine`, { withCredentials: true })
        .then((res) => {
          setStats(res.data);
          // Celebrate the FIRST redemption in the last 7 days — deduped via
          // localStorage so it doesn't fire again on every mount/poll.
          const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const redeemedThisWeek = (res.data.vouchers || []).filter(
            (v) => v.redeemed && v.redeemed_at && new Date(v.redeemed_at).getTime() >= weekAgo
          );
          if (redeemedThisWeek.length === 1) {
            const voucher = redeemedThisWeek[0];
            if (localStorage.getItem(storageKey) !== voucher.id) {
              toast.success(`First voucher redemption this week — ${voucher.code}!`, { icon: '🔥', duration: 5000 });
              localStorage.setItem(storageKey, voucher.id);
            }
          }
        })
        .catch(() => setStats(null));
    };
    fetchStats();
    // A lead can redeem this agent's voucher at any moment while they're
    // sitting on the page — poll so the count/streak never needs a reload.
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [admin?.username]);

  if (!stats || stats.total_codes === 0) return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 rounded-lg text-sm font-medium"
      data-testid="voucher-performance-stat"
      title="Your voucher codes' redemption performance"
    >
      <Ticket className="w-4 h-4" /> {stats.redeemed_count}/{stats.total_codes} vouchers redeemed
      {stats.current_streak >= 2 && (
        <span
          className="inline-flex items-center gap-0.5 text-amber-600 font-semibold"
          data-testid="voucher-performance-streak"
          title={`${stats.current_streak} redemptions in a row`}
        >
          <Flame className="w-3.5 h-3.5" /> {stats.current_streak}
        </span>
      )}
    </span>
  );
};

export default VoucherPerformanceStat;
